import { makeAutoObservable, runInAction } from 'mobx';

import { assistantStore } from '@/app/editor/ai-assistant/assistantStore';
import {
  getDefaultAiAssistantEnabled,
  isSdkAgentChatFeatureEnabled,
} from '@/app/editor/featureFlags';
import { sdk } from '@/app/sdk';
import { project } from '@/editor/store';

export { getDefaultAiAssistantEnabled } from '@/app/editor/featureFlags';

/** Default when `app.genesysMcpEnabled` is not stored: on in all builds. */
export function getDefaultGenesysMcpEnabled(): boolean {
  return true;
}

/** Whether the docked AI assistant column and title bar entry should be shown. */
export function isAiAssistantUiEnabled(): boolean {
  return appPreferencesStore.aiAssistantEnabled
    && isSdkAgentChatFeatureEnabled();
}

const UI_BACKGROUND_OPACITY_KEY = 'genesys-ui-background-opacity';
export const DEFAULT_UI_BACKGROUND_OPACITY = 0.7;
const MIN_UI_BACKGROUND_OPACITY = 0.3;
const MAX_UI_BACKGROUND_OPACITY = 1.0;

function readUiBackgroundOpacityFromStorage(): number {
  try {
    const raw = typeof window !== 'undefined'
      ? localStorage.getItem(UI_BACKGROUND_OPACITY_KEY)
      : null;
    if (raw === null) return DEFAULT_UI_BACKGROUND_OPACITY;
    const parsed = parseFloat(raw);
    if (!isFinite(parsed)) return DEFAULT_UI_BACKGROUND_OPACITY;
    return Math.min(MAX_UI_BACKGROUND_OPACITY, Math.max(MIN_UI_BACKGROUND_OPACITY, parsed));
  } catch {
    return DEFAULT_UI_BACKGROUND_OPACITY;
  }
}

class AppPreferencesStore {
  aiAssistantEnabled = getDefaultAiAssistantEnabled();
  genesysMcpEnabled = getDefaultGenesysMcpEnabled();
  isLoaded = false;
  appSettingsDialogOpen = false;
  uiBackgroundOpacity = DEFAULT_UI_BACKGROUND_OPACITY;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    this.uiBackgroundOpacity = readUiBackgroundOpacityFromStorage();
  }

  openAppSettings(): void {
    this.appSettingsDialogOpen = true;
  }

  closeAppSettings(): void {
    this.appSettingsDialogOpen = false;
  }

  setUiBackgroundOpacity(value: number): void {
    const clamped = Math.min(MAX_UI_BACKGROUND_OPACITY, Math.max(MIN_UI_BACKGROUND_OPACITY, value));
    this.uiBackgroundOpacity = clamped;
    try {
      localStorage.setItem(UI_BACKGROUND_OPACITY_KEY, String(clamped));
    } catch {
      // ignore write failures (e.g. private browsing storage quota)
    }
  }

  async load(): Promise<void> {
    if (!sdk?.app?.getState) {
      runInAction(() => {
        this.isLoaded = true;
      });
      return;
    }

    try {
      const [aiStored, mcpStored] = await Promise.all([
        sdk.app.getState('app.aiAssistantEnabled'),
        sdk.app.getState('app.genesysMcpEnabled'),
      ]);
      runInAction(() => {
        if (aiStored === false) {
          this.aiAssistantEnabled = false;
        } else if (aiStored === true) {
          this.aiAssistantEnabled = true;
        } else {
          this.aiAssistantEnabled = getDefaultAiAssistantEnabled();
        }

        if (mcpStored === false) {
          this.genesysMcpEnabled = false;
        } else if (mcpStored === true) {
          this.genesysMcpEnabled = true;
        } else {
          this.genesysMcpEnabled = getDefaultGenesysMcpEnabled();
        }

        this.isLoaded = true;
      });
    } catch (error) {
      console.error('[AppPreferences] Failed to load app preferences:', error);
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  private async refreshGateway(): Promise<void> {
    const gateway = sdk?.gateway;
    if (!gateway) {
      return;
    }

    const projectPath = project.projectFolder;
    if (!projectPath || (!this.aiAssistantEnabled && !this.genesysMcpEnabled)) {
      try {
        await gateway.stop();
      } catch {
        // ignore stop failures
      }
      return;
    }

    try {
      await gateway.restart(projectPath);
    } catch (error) {
      console.error('[AppPreferences] Failed to restart gateway:', error);
    }
  }

  async setAiAssistantEnabled(enabled: boolean): Promise<void> {
    runInAction(() => {
      this.aiAssistantEnabled = enabled;
    });

    if (!enabled) {
      assistantStore.detach();
    }

    try {
      if (sdk?.app?.setState) {
        await sdk.app.setState('app.aiAssistantEnabled', enabled);
      }
      await this.refreshGateway();
    } catch (error) {
      console.error('[AppPreferences] Failed to save AI assistant preference:', error);
    }
  }

  async setGenesysMcpEnabled(enabled: boolean): Promise<void> {
    runInAction(() => {
      this.genesysMcpEnabled = enabled;
    });

    try {
      if (sdk?.app?.setState) {
        await sdk.app.setState('app.genesysMcpEnabled', enabled);
      }
      await this.refreshGateway();
    } catch (error) {
      console.error('[AppPreferences] Failed to save Genesys MCP preference:', error);
    }
  }
}

export const appPreferencesStore = new AppPreferencesStore();
